import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, ApiError } from "../api/client";
import { useRuntimeStatus } from "../api/runtimeStatusContext";
import { failurePresentation } from "../lib/operationPresentation";
import { TokenPair } from "./TokenPair";
import { priceFrame } from "../lib/priceFrame";
import { formatMoney, shorten } from "../lib/format";
import type { ClosePositionRequest, Operation, OperationFailure, Position } from "@auto-pools/core";

export function PositionActionModal({
  title,
  description,
  endpoint,
  payload,
  submitLabel,
  onClose,
  onAccepted,
  position,
  action,
}: {
  title: string;
  description: string;
  endpoint: string;
  payload?: Pick<ClosePositionRequest, "cleanupOnly">;
  submitLabel: string;
  onClose: () => void;
  onAccepted?: (operation: Operation) => void;
  position?: Position;
  action?: {
    pending: boolean;
    error: string | null;
    failure?: OperationFailure | null;
    submit: () => Promise<void>;
  };
}) {
  const inFlight = useRef(false),
    dialog = useRef<HTMLDialogElement>(null),
    keep = useRef<HTMLButtonElement>(null);
  const [pending, setPending] = useState(false),
    [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<OperationFailure | null>(null);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []),
    runtime = useRuntimeStatus();
  const cleanup = payload?.cleanupOnly === true;
  const busy = action?.pending ?? pending;
  useEffect(() => {
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    keep.current?.focus();
    return () => {
      element?.close();
      const fallback = position ? document.getElementById(`position-heading-${position.id}`) : null;
      if (previous instanceof HTMLElement && previous.isConnected && !previous.matches(":disabled"))
        previous.focus();
      else (fallback ?? document.querySelector<HTMLElement>(".positions-toolbar button"))?.focus();
    };
  }, [position]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || inFlight.current || !runtime.executionReady) return;
    if (action) {
      await action.submit();
      return;
    }
    inFlight.current = true;
    setPending(true);
    setError(null);
    setFailure(null);
    try {
      const response = await api<Operation | { operation: Operation }>(endpoint, {
        method: "POST",
        body: { ...payload, idempotencyKey },
        sensitive: true,
      });
      onAccepted?.("operation" in response ? response.operation : response);
      onClose();
    } catch (reason) {
      setFailure(reason instanceof ApiError ? (reason.operationFailure ?? null) : null);
      setError(reason instanceof Error ? reason.message : "Command was not accepted");
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }
  const reportedFailure = action?.failure ?? failure;
  const failureCopy = reportedFailure
    ? failurePresentation(
        { kind: "close", step: cleanup ? "cleanup" : reportedFailure.step },
        reportedFailure,
      )
    : null;
  const money = (value: string | null) => (value === null ? "—" : formatMoney(value));
  const target =
    position &&
    ([position.tokenA, position.tokenB].find((t) => t.mint === position.exitPolicy.targetMint)
      ?.symbol ??
      shorten(position.exitPolicy.targetMint));
  const pnl = position?.pnlBreakdown
    ? position.pnlBreakdown.status === "available"
      ? position.pnlBreakdown.netPnlUsd
      : null
    : (position?.pnlUsd ?? null);
  return (
    <dialog
      ref={dialog}
      className="position-action-modal"
      aria-labelledby="action-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          const r = e.currentTarget.getBoundingClientRect();
          if (
            e.clientX < r.left ||
            e.clientX > r.right ||
            e.clientY < r.top ||
            e.clientY > r.bottom
          )
            onClose();
        }
      }}
    >
      <header className="modal-header">
        {position && <TokenPair frame={priceFrame(position.tokenA, position.tokenB)} />}
        <div>
          <h2 id="action-title">
            {position ? (cleanup ? "Cancel opening?" : "Close position?") : title}
          </h2>
          {position && (
            <small>
              {position.pair} · {shorten(position.id, 7)} ·{" "}
              {position.monitorState === "opening"
                ? "opening"
                : position.inRange === null
                  ? "range unknown"
                  : position.inRange
                    ? "in range"
                    : "out of range"}
            </small>
          )}
        </div>
        <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <form onSubmit={(e) => void submit(e)}>
        <div className="position-action-body">
          <p>
            {position
              ? cleanup
                ? "Cancelling closes the verified empty position account."
                : "Withdraws liquidity and unclaimed fees, then converts the pair tokens within your saved limits."
              : description}
          </p>
          {position &&
            (cleanup ? (
              <ul className="confirmation-facts">
                <li>Funds are released in their current tokens</li>
                <li>Eligible account rent returns to the wallet</li>
                <li>Network fees already paid are not refunded</li>
                <li>No swap is made</li>
              </ul>
            ) : (
              <dl className="confirmation-facts">
                <div>
                  <dt>Value now · LP value</dt>
                  <dd>{money(position.balanceUsd)}</dd>
                </div>
                <div>
                  <dt>Indicative net P&amp;L USD</dt>
                  <dd>{money(pnl)}</dd>
                </div>
                <div>
                  <dt>Unclaimed fees USD</dt>
                  <dd>{money(position.pendingFeesUsd)}</dd>
                </div>
                <div>
                  <dt>Return token</dt>
                  <dd>{target}</dd>
                </div>
              </dl>
            ))}
          {position && (
            <p className="confirmation-note">
              {cleanup
                ? "Cancellation is refused if the deposit has funded or its outcome is unknown."
                : "P&L uses current indicative marks and excludes future execution costs. Closing pauses if the price exceeds your saved swap limits."}
            </p>
          )}
          {!runtime.executionReady && (
            <p className="inline-alert warning" role="status">
              Executor unavailable. A new command is currently unavailable.
            </p>
          )}
          {(action?.error ?? error) && (
            <div className="inline-alert warning" role="alert">
              {failureCopy ? (
                <>
                  <strong>{failureCopy[0]}</strong>
                  <p>{failureCopy[1]}</p>
                  <details>
                    <summary>Details</summary>
                    {action?.error ?? error}
                  </details>
                </>
              ) : (
                (action?.error ?? error)
              )}
            </div>
          )}
        </div>
        <footer>
          <button ref={keep} type="button" className="button" onClick={onClose}>
            {position ? (cleanup ? "Keep opening" : "Keep position") : "Back"}
          </button>
          <button className="button danger" disabled={busy || !runtime.executionReady}>
            {busy
              ? position
                ? cleanup
                  ? "Cancelling…"
                  : "Closing…"
                : "Resuming…"
              : position
                ? cleanup
                  ? "Cancel opening"
                  : `Close → ${target ?? "—"}`
                : submitLabel}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
